<template>
  <div id="app" :class="{ 'mobile-drawer-open': isCategoryMenuOpen || isAccountMenuOpen }">
    <!-- Add toast notification component -->
    <ToastNotification />

    <!-- Add Login Modal -->
    <Modal :show="showLoginModal" @close="showLoginModal = false">
      <Login @login-success="handleLoginSuccess" />
    </Modal>

    <!-- Add Signup Modal -->
    <Modal :show="showSignupModal" @close="showSignupModal = false">
      <Signup @signup-success="handleSignupSuccess" />
    </Modal>

    <!-- Image Search Modal -->
    <ImageSearchModal :show="showImageSearchModal" @close="closeImageSearch" />

    <!-- Route transition loading indicator. Lazy-loaded route components (like
         CategoryListing) can take a while to download their chunk before they
         even mount, which used to leave the screen blank with no feedback. -->
    <div v-if="isRouteLoading" class="route-loading-overlay">
      <div class="spinner"></div>
    </div>

    <header>
      <nav>
        <!-- Mobile Categories Button (left) -->
        <button class="mobile-icon-btn mobile-categories-btn" :class="{ 'active': isCategoryMenuOpen }" @click="toggleCategoryMenu" aria-label="Categories">
          <i class="fas fa-bars"></i>
        </button>

        <div class="logo">
          <router-link to="/">
            <img src="/curatoreden_logo.png" alt="Curatoreden Logo" class="main-logo">
          </router-link>
        </div>

        <!-- Mobile right-side icon group -->
        <div class="mobile-icon-group">
          <!-- Mobile Search Icon -->
          <button class="mobile-icon-btn mobile-search-icon" @click="toggleSearchBar" aria-label="Search">
            <i class="fas fa-search"></i>
          </button>

          <!-- Cart Icon for Mobile -->
          <router-link to="/cart" class="mobile-icon-btn cart-icon mobile-cart" aria-label="Cart">
            <span class="cart-count" v-if="cartItemCount > 0">{{ cartItemCount }}</span>
            <i class="fas fa-shopping-bag"></i>
          </router-link>

          <!-- Mobile Account Button (right) -->
          <button class="mobile-icon-btn mobile-account-btn" :class="{ 'active': isAccountMenuOpen }" @click="toggleAccountMenu" aria-label="Account">
            <i class="fas fa-user"></i>
          </button>
        </div>

        <!-- Main Category Nav (desktop) -->
        <div class="category-nav">
          <div
            v-for="category in mainCategories"
            :key="`cat-${category.slug}`"
            class="category-nav-item"
            @mouseenter="openCategoryDropdown(category.slug)"
            @mouseleave="closeCategoryDropdown"
          >
            <router-link
              :to="categoryTo(category)"
              class="category-nav-link"
              :aria-expanded="openCategorySlug === category.slug ? 'true' : 'false'"
              @focus.native="openCategoryDropdown(category.slug)"
            >
              {{ category.name }}
            </router-link>

            <!-- Categories in the left column, brands for the hovered category
                 in the right column — a proper two-column mega menu instead of
                 a single stacked list. -->
            <div
              class="category-dropdown"
              v-if="openCategorySlug === category.slug && hasDropdown(category)"
            >
              <CategoryMegaMenu :category="category" @navigate="closeCategoryDropdown" />
            </div>
          </div>
        </div>

        <!-- Search Container -->
        <div
          class="search-container"
          :class="{ 'mobile-search-active': isSearchBarOpen }"
          ref="searchContainer"
        >
          <div class="search-input-wrapper">
            <div class="search-icon-wrapper">
              <i class="fas" :class="[searchQuery ? 'fa-times' : 'fa-search']" @click="handleSearchIcon"></i>
            </div>
            <input
              v-model="searchQuery"
              @keyup.enter="search"
              @focus="isSearchFocused = true"
              @input="fetchSuggestions"
              @keydown.esc="closeAutocomplete"
              type="text"
              placeholder="Search for anything"
              ref="searchInput"
            />
            <button class="camera-btn" title="Image search" @click="openImageSearch">
              <i class="fas fa-camera"></i>
            </button>
          </div>
          <button class="search-btn" @click="search">Search</button>

          <!-- Predictive dropdown (T10). It anchors to .search-container, which is
               already position:relative on desktop and a fixed overlay on mobile. -->
          <SearchAutocomplete
            :query="searchQuery"
            :open="isAutocompleteOpen"
            :results="autocompleteResults"
            :loading="isAutocompleteLoading"
            @select="handleAutocompleteSelect"
            @search-external="searchExternal"
          />
        </div>

        <!-- Mobile Categories Drawer (left) -->
        <div class="mobile-drawer mobile-drawer-left" :class="{ 'drawer-active': isCategoryMenuOpen }">
          <button class="close-btn mobile-menu-close" @click="closeCategoryMenu">
            <i class="fas fa-times"></i>
          </button>

          <div class="mobile-menu" v-if="isCategoryMenuOpen" @click.stop>
            <div class="mobile-category-list">
              <div
                v-for="category in mainCategories"
                :key="`m-cat-${category.slug}`"
                class="mobile-category"
              >
                <button
                  class="mobile-category-header"
                  :aria-expanded="expandedMobileCategory === category.slug ? 'true' : 'false'"
                  @click="toggleMobileCategory(category.slug)"
                >
                  <span>{{ category.name }}</span>
                  <i
                    class="fas"
                    :class="expandedMobileCategory === category.slug ? 'fa-chevron-up' : 'fa-chevron-down'"
                  ></i>
                </button>

                <div
                  class="mobile-category-brands"
                  v-if="expandedMobileCategory === category.slug"
                >
                  <!-- Closing is wired per-link, not on this wrapper: a wrapper
                       handler also fired for the subcategory twisties bubbling
                       up, so expanding a subcategory shut the whole drawer. -->
                  <router-link :to="categoryTo(category)" class="mobile-brand-item is-all" @click.native="closeCategoryMenu">
                    All {{ category.name }}
                  </router-link>

                  <!-- Subcategories carry their own twisty (NavCategoryNode), so
                       each one collapses independently. Brands are never shown
                       in the mobile drawer, even when they're filed directly on
                       a root/top-level category. -->
                  <div class="mobile-subcategories">
                    <NavCategoryNode
                      v-for="child in category.children"
                      :key="`m-sub-${child.id}`"
                      :node="child"
                      @navigate="closeCategoryMenu"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Mobile Account Drawer (right) -->
        <div class="mobile-drawer mobile-drawer-right" :class="{ 'drawer-active': isAccountMenuOpen }">
          <button class="close-btn mobile-menu-close" @click="closeAccountMenu">
            <i class="fas fa-times"></i>
          </button>

          <div class="mobile-menu" v-if="isAccountMenuOpen" @click.stop>
            <!-- Account section (logged in only) -->
            <template v-if="isLoggedIn">
            <div class="mobile-profile">
              <div class="mobile-profile-image">
                <i class="fas fa-user"></i>
              </div>
              <div class="mobile-profile-info">
                <div class="mobile-profile-name">{{ user?.username || 'User' }}</div>
                <router-link to="/profile" class="mobile-profile-view" @click="closeAccountMenu">

                </router-link>
              </div>
            </div>

            <div class="mobile-menu-list" @click="closeAccountMenu">
              <router-link to="/profile" class="mobile-menu-item">
                <i class="fas fa-user"></i> My Profile
              </router-link>
              <router-link to="/profile/favourites" class="mobile-menu-item">
                <i class="fas fa-heart"></i> Favourites
              </router-link>
              <router-link to="/change-password" class="mobile-menu-item">
                <i class="fas fa-key"></i> Change Password
              </router-link>
              <router-link to="/purchases" class="mobile-menu-item">
                <i class="fas fa-shopping-bag"></i> Purchases
              </router-link>
              <router-link to="/addresses" class="mobile-menu-item">
                <i class="fas fa-map-marker-alt"></i> Addresses
              </router-link>
              <router-link :to="user?.role === 'admin' ? '/admin/image-requests' : '/profile/requests'" class="mobile-menu-item">
                <i class="fas fa-image"></i> Image Search Requests
              </router-link>
              <router-link v-if="user?.role === 'admin'" to="/admin/home" class="mobile-menu-item">
                <i class="fas fa-cog"></i> Admin Dashboard
              </router-link>
              <router-link v-if="user?.role === 'admin'" to="/admin/category-tree" class="mobile-menu-item">
                <i class="fas fa-sitemap"></i> Category Tree
              </router-link>
              <router-link v-if="user?.role === 'admin'" to="/admin/products" class="mobile-menu-item">
                <i class="fas fa-box"></i> Products
              </router-link>
              <a href="#" class="mobile-menu-item" @click.prevent="handleLogout">
                <i class="fas fa-sign-out-alt"></i> Logout
              </a>
            </div>
            </template>

            <!-- Auth actions (logged out only) -->
            <div class="mobile-auth-actions" v-else>
              <button class="login-btn" @click="closeAccountMenu(); showLoginModal = true">Login</button>
              <button class="signup-btn" @click="closeAccountMenu(); showSignupModal = true">Sign Up</button>
            </div>
          </div>
        </div>

        <!-- Desktop Navigation -->
        <div class="desktop-nav">
          <!-- Regular Nav Links for Desktop -->
          <div class="nav-links">
            <template v-if="isLoggedIn">
              <div class="profile-menu">
                <div 
                  class="username" 
                  :class="{ active: showProfileMenu }"
                  @click="toggleProfileMenu"
                >
                  {{ user?.username || 'User' }}
                  <i class="fas fa-chevron-down"></i>
                </div>
                <div class="profile-dropdown" :class="{ show: showProfileMenu }">
                  <router-link to="/profile" class="dropdown-item">
                    <i class="fas fa-user"></i> My Profile
                  </router-link>
                  <router-link to="/profile/favourites" class="dropdown-item">
                    <i class="fas fa-heart"></i> Favourites
                  </router-link>
                  <router-link to="/change-password" class="dropdown-item">
                    <i class="fas fa-key"></i> Change Password
                  </router-link>
                  <router-link to="/purchases" class="dropdown-item">
                    <i class="fas fa-shopping-bag"></i> Purchases
                  </router-link>
                  <router-link to="/addresses" class="dropdown-item">
                    <i class="fas fa-map-marker-alt"></i> Addresses
                  </router-link>
                  <router-link :to="user?.role === 'admin' ? '/admin/image-requests' : '/profile/requests'" class="dropdown-item">
                    <i class="fas fa-image"></i> Image Search Requests
                  </router-link>
                  <div class="divider"></div>
                  <a href="#" @click.prevent="logout">
                    <i class="fas fa-sign-out-alt"></i> Logout
                  </a>
                </div>
              </div>
              <router-link v-if="user?.role === 'admin'" to="/admin/home" class="admin-link">
                <i class="fas fa-cog"></i> Admin
              </router-link>
              <router-link v-if="user?.role === 'admin'" to="/admin/category-tree" class="admin-link">
                <i class="fas fa-sitemap"></i> Category Tree
              </router-link>
              <router-link v-if="user?.role === 'admin'" to="/admin/products" class="admin-link">
                <i class="fas fa-box"></i> Products
              </router-link>
            </template>
            <template v-else>
              <button @click="showLoginModal = true" class="login-btn">Login</button>
              <button @click="showSignupModal = true" class="signup-btn">Sign Up</button>
            </template>
            <router-link to="/cart" class="cart-icon">
              <span class="cart-count" v-if="cartItemCount > 0">{{ cartItemCount }}</span>
              <i class="fas fa-shopping-bag"></i>
            </router-link>
          </div>
        </div>
      </nav>
    </header>
    <!-- <main style="padding-bottom: 48px;"> -->
      <main>
      <div v-if="isLoggedIn && isProfilePage" class="profile-page-layout">
        <div class="profile-sidebar">
          <h2>My Account</h2>
          <div class="profile-nav">
            <router-link to="/profile" exact>
              <i class="fas fa-user"></i> Profile
            </router-link>
            <router-link to="/profile/favourites">
              <i class="fas fa-heart"></i> Favourites
            </router-link>
            <router-link to="/change-password">
              <i class="fas fa-key"></i> Change Password
            </router-link>
            <router-link to="/purchases">
              <i class="fas fa-shopping-bag"></i> Purchases
            </router-link>
            <router-link to="/addresses">
              <i class="fas fa-map-marker-alt"></i> Addresses
            </router-link>
            <router-link :to="user && user.role === 'admin' ? '/admin/image-requests' : '/profile/requests'">
              <i class="fas fa-image"></i> Image Search Requests
            </router-link>
            <!--
            <router-link to="/notifications">
              <i class="fas fa-bell"></i> Notifications
            </router-link>
            <router-link to="/settings">
              <i class="fas fa-cog"></i> Settings
            </router-link>
            -->
          </div>
        </div>
        <div class="profile-content">
          <router-view></router-view>
        </div>
      </div>
      <router-view v-else></router-view>


    </main>
      <!-- Footer -->
      <footer class="main-footer">
        <div class="footer-links">
          <router-link to="/privacy-policy" class="footer-link">Privacy Policy</router-link>
          <router-link to="/refund-policy" class="footer-link">Refund and Returns Policy</router-link>
          <router-link to="/terms" class="footer-link">Terms and Conditions</router-link>
        </div>
        <div class="footer-copyright">
          Curatoreden © {{ currentYear }}
        </div>
      </footer>
  </div>
</template>

<script>
import ToastNotification from './components/ToastNotification.vue';
import Modal from './components/Modal.vue';
import Login from './views/Login.vue';
import Signup from './views/Signup.vue';
import tokenService from './services/tokenService';
import favouritesService from './services/favouritesService';
import axios from 'axios';
import config from './config';
import ImageSearchModal from './components/ImageSearchModal.vue';
import NavCategoryNode from './components/NavCategoryNode.vue';
import CategoryMegaMenu from './components/CategoryMegaMenu.vue';
import SearchAutocomplete from './components/SearchAutocomplete.vue';

// Configure axios
axios.defaults.withCredentials = true;

export default {
  name: 'App',
  components: {
    ToastNotification,
    Modal,
    Login,
    Signup,
    ImageSearchModal,
    NavCategoryNode,
    CategoryMegaMenu,
    SearchAutocomplete
  },
  data() {
    return {
      searchQuery: '',
      // T10: dropdown frame state. `autocompleteResults` stays empty until T12
      // supplies in-site matching; the section renders its empty state meanwhile.
      isSearchFocused: false,
      autocompleteResults: [],
      isAutocompleteLoading: false,
      suggestTimer: null,
      // Guards against an out-of-order response overwriting fresher results.
      suggestRequestId: 0,
      showImageSearchModal: false,
      isLoggedIn: false,
      user: null,
      isCategoryMenuOpen: false,
      isAccountMenuOpen: false,
      isSearchBarOpen: false,
      isRouteLoading: false,
      routeLoadingTimeout: null,
      inactivityTimer: null,
      lastActivity: Date.now(),
      showLoginModal: false,
      showSignupModal: false,
      isLoading: true,
      isAuthenticated: false,
      showProfileMenu: false,
      // T8: the nav is driven by the live tree, not a hardcoded config.
      mainCategories: [],
      openCategorySlug: null,
      expandedMobileCategory: null,
      config
    }
  },
  
  computed: {
    cartItemCount() {
      return this.$store.getters.cartItemCount;
    },
    isProfilePage() {
      return [
        '/profile',
        '/profile/requests',
        '/profile/favourites',
        '/purchases',
        '/addresses',
        '/change-password',
        '/admin/image-requests'
      ].includes(this.$route.path);
    },
    showMobileMenu() {
      return this.isCategoryMenuOpen || this.isAccountMenuOpen;
    },
    currentYear() {
      return new Date().getFullYear();
    },
    // The dropdown is a frame around the CTA, so a non-empty query is enough to
    // show it — it stays useful even while Our Selection has nothing to offer.
    isAutocompleteOpen() {
      return this.isSearchFocused && this.searchQuery.trim().length > 0;
    }
  },
  created() {
    this.checkSession();
    this.loadCategories();
  },
  mounted() {
    // Surface the route-loading spinner while a lazy route chunk (or its guards)
    // is still resolving. Delayed slightly so an already-cached/fast navigation
    // doesn't just flash the spinner.
    this.unregisterRouteLoadingGuards = [
      this.$router.beforeEach((to, from, next) => {
        clearTimeout(this.routeLoadingTimeout);
        this.routeLoadingTimeout = setTimeout(() => {
          this.isRouteLoading = true;
        }, 150);
        next();
      }),
      this.$router.afterEach(() => {
        clearTimeout(this.routeLoadingTimeout);
        this.isRouteLoading = false;
      })
    ].filter((unregister) => typeof unregister === 'function');
    // onError has no unregister handle, but App is the root component and
    // lives for the whole SPA session, so this permanent registration is fine.
    this.$router.onError(() => {
      clearTimeout(this.routeLoadingTimeout);
      this.isRouteLoading = false;
    });

    // Add event listeners for user activity
    window.addEventListener('mousemove', this.resetInactivityTimer);
    window.addEventListener('keydown', this.resetInactivityTimer);
    window.addEventListener('click', this.handleGlobalClick);
    window.addEventListener('scroll', this.resetInactivityTimer);
    
    // Add storage event listener to detect login/logout
    window.addEventListener('storage', this.handleStorageChange);
    
    // Listen for auth state changes from Login and Signup components
    this.$root.$on('auth-state-changed', async ({ isLoggedIn, user }) => {
      this.isLoggedIn = isLoggedIn;
      this.user = user;

      // If user just logged in, apply any favourites the user tried to add while logged out.
      if (isLoggedIn) {
        try {
          const { appliedCount } = await favouritesService.applyPendingFavourites();
          if (appliedCount > 0) this.$root.$emit('favourites-changed');
        } catch (e) {
          // ignore
        }
      }
    });

    // Listen for open login modal event
    this.$root.$on('open-login-modal', () => {
      // Close signup modal first
      this.showSignupModal = false;
      // Small delay to allow transition
      setTimeout(() => {
        this.showLoginModal = true;
      }, 150);
    });

    // Listen for open signup modal event
    this.$root.$on('open-signup-modal', () => {
      // Close login modal first
      this.showLoginModal = false;
      // Small delay to allow transition
      setTimeout(() => {
        this.showSignupModal = true;
      }, 150);
    });

    document.addEventListener('click', this.handleClickOutside);
  },
  beforeDestroy() {
    // Clear the inactivity timer
    if (this.inactivityTimer) {
      clearInterval(this.inactivityTimer);
    }

    clearTimeout(this.routeLoadingTimeout);
    (this.unregisterRouteLoadingGuards || []).forEach((unregister) => unregister());

    // A pending suggest debounce would fire against a torn-down component.
    clearTimeout(this.suggestTimer);
    
    // Remove event listeners
    window.removeEventListener('mousemove', this.resetInactivityTimer);
    window.removeEventListener('keydown', this.resetInactivityTimer);
    window.removeEventListener('click', this.handleGlobalClick);
    window.removeEventListener('scroll', this.resetInactivityTimer);
    window.removeEventListener('storage', this.handleStorageChange);
    
    // Remove auth state change listener
    this.$root.$off('auth-state-changed');
    this.$root.$off('open-login-modal');
    this.$root.$off('open-signup-modal');

    document.removeEventListener('click', this.handleClickOutside);
  },
  methods: {
    async checkSession() {
      try {
        const response = await axios.get(`${config.apiBaseUrl}/api/auth/check-session`, {
          withCredentials: true
        });
        this.isAuthenticated = response.data.isAuthenticated;
        this.user = response.data.user;
        
        console.log('Session check successful:', { 
          isAuthenticated: this.isAuthenticated, 
          user: this.user 
        });
        
        // Emit auth state change
        this.$root.$emit('auth-state-changed', {
          isLoggedIn: this.isAuthenticated,
          user: this.user
        });

        if (this.isAuthenticated) {
          try {
            const { appliedCount } = await favouritesService.applyPendingFavourites();
            if (appliedCount > 0) this.$root.$emit('favourites-changed');
          } catch (e) {
            // ignore
          }
        }
      } catch (error) {
        // 401 is expected for non-logged-in users
        if (error.response && error.response.status === 401) {
          console.log('User not logged in (expected)');
          this.isAuthenticated = false;
          this.user = null;
        } else {
          console.error('Session check error:', error);
          if (error.response) {
            console.error('Response data:', error.response.data);
            console.error('Response status:', error.response.status);
          }
          this.isAuthenticated = false;
          this.user = null;
        }
        
        // Clear stored data - now session-based
      } finally {
        this.isLoading = false;
      }
    },
    // Reset inactivity timer on user action
    resetInactivityTimer() {
      this.lastActivity = Date.now();
    },
    
    // Handle storage changes (login/logout)
    handleStorageChange(event) {
      // Session-based auth - no localStorage handling needed
    },
    handleResize() {
      if (window.innerWidth > 768) {
        this.isCategoryMenuOpen = false;
        this.isAccountMenuOpen = false;
        this.isSearchBarOpen = false;
      }
    },
    toggleCategoryMenu() {
      this.isCategoryMenuOpen = !this.isCategoryMenuOpen;
      if (this.isCategoryMenuOpen) {
        this.isAccountMenuOpen = false;
        this.isSearchBarOpen = false;
      }
    },
    closeCategoryMenu() {
      this.isCategoryMenuOpen = false;
      this.expandedMobileCategory = null;
    },
    toggleAccountMenu() {
      this.isAccountMenuOpen = !this.isAccountMenuOpen;
      if (this.isAccountMenuOpen) {
        this.isCategoryMenuOpen = false;
        this.isSearchBarOpen = false;
      }
    },
    closeAccountMenu() {
      this.isAccountMenuOpen = false;
    },
    closeMobileMenu() {
      this.isCategoryMenuOpen = false;
      this.isAccountMenuOpen = false;
      this.isSearchBarOpen = false;
      this.expandedMobileCategory = null;
    },
    // Paths come from the category's materialized `path` ('/women/bags/'), so a
    // link is correct at any depth without the nav knowing the shape of the tree.
    toCategoryPath(path) {
      return `/c/${String(path || '').split('/').filter(Boolean).join('/')}`;
    },
    categoryTo(category) {
      return this.toCategoryPath(category.path);
    },
    hasDropdown(category) {
      return (category.brands && category.brands.length > 0)
        || (category.children && category.children.length > 0);
    },
    async loadCategories() {
      try {
        const response = await axios.get(`${config.apiBaseUrl}/api/categories`);
        this.mainCategories = response.data?.tree || [];
      } catch (err) {
        // The nav is not worth breaking the page over; it just stays empty.
        console.warn('Failed to load navigation categories', err);
        this.mainCategories = [];
      }
    },
    openCategoryDropdown(slug) {
      this.openCategorySlug = slug;
    },
    closeCategoryDropdown() {
      this.openCategorySlug = null;
    },
    toggleMobileCategory(slug) {
      this.expandedMobileCategory = this.expandedMobileCategory === slug ? null : slug;
    },
    toggleSearchBar() {
      this.isSearchBarOpen = !this.isSearchBarOpen;
      if (this.isSearchBarOpen) {
        this.isCategoryMenuOpen = false;
        this.isAccountMenuOpen = false;
        // Add a small delay to ensure the search bar is visible before focusing
        setTimeout(() => {
          this.$refs.searchInput.focus();
        }, 100);
      }
    },
    closeAutocomplete() {
      this.isSearchFocused = false;
    },
    // T12: Our Selection. Debounced because this fires on every keystroke, and
    // stamped so a slow response for an earlier query cannot overwrite a newer one.
    fetchSuggestions() {
      const query = this.searchQuery.trim();
      if (!query) {
        this.autocompleteResults = [];
        this.isAutocompleteLoading = false;
        return;
      }

      clearTimeout(this.suggestTimer);
      this.suggestTimer = setTimeout(async () => {
        const requestId = ++this.suggestRequestId;
        this.isAutocompleteLoading = true;
        try {
          const response = await axios.get(`${config.apiBaseUrl}/api/search/suggest`, {
            params: { q: query, limit: 50 }
          });
          if (requestId !== this.suggestRequestId) return;
          this.autocompleteResults = response.data?.suggestions || [];
        } catch (err) {
          if (requestId !== this.suggestRequestId) return;
          // The CTA is still useful on its own; an empty section is the fallback.
          this.autocompleteResults = [];
        } finally {
          if (requestId === this.suggestRequestId) this.isAutocompleteLoading = false;
        }
      }, 200);
    },
    // T11 will own the external hand-off; today it is the same route the search
    // button already uses, which is what keeps the raw string intact.
    searchExternal(rawQuery) {
      this.searchQuery = rawQuery;
      this.search();
    },
    // T12 supplies real results; selecting one should route to the product. Until
    // then nothing emits this, so closing the dropdown is the whole behaviour.
    handleAutocompleteSelect(result) {
      this.closeAutocomplete();
      if (result && result.path) {
        this.$router.push(result.path).catch(err => {
          if (err.name !== 'NavigationDuplicated') throw err;
        });
      }
    },
    search() {
      this.closeAutocomplete();
      if (this.searchQuery.trim()) {
        const query = this.searchQuery.trim();
        this.$router.push({
          name: 'Search',
          query: { q: query }
        }).catch(err => {
          if (err.name !== 'NavigationDuplicated') {
            throw err;
          }
        });
        this.searchQuery = '';
        this.closeMobileMenu();
      }
    },
    async logout() {
      try {
        // Clear all local data first
        this.isAuthenticated = false;
        this.user = null;
        // Session-based auth - no localStorage needed
        
        // Call the logout API
        await axios.post(`${config.apiBaseUrl}/api/auth/logout`, {}, {
          withCredentials: true
        });
        
        // Emit auth state change
        this.$root.$emit('auth-state-changed', {
          isLoggedIn: false,
          user: null
        });
        
        // Close any open menus
        this.showProfileMenu = false;
        this.closeMobileMenu();
        
        localStorage.removeItem('user');
        localStorage.removeItem('cart');
        localStorage.removeItem('cartWithParams');
        localStorage.removeItem('selectedAddressId');
        localStorage.removeItem('authToken');
        // Navigate to home page
        if (this.$route.path !== '/') {
          try {
            await this.$router.push('/');
          } catch (navigationError) {
            if (navigationError.name !== 'NavigationDuplicated') {
              throw navigationError;
            }
          }
        }
      } catch (error) {
        console.error('Logout error:', error);
        // Even if the API call fails, clear local data
        this.isAuthenticated = false;
        this.user = null;
        // Session-based auth - no localStorage needed
      }
    },
    handleSearchIcon() {
      if (this.searchQuery) {
        // If there's text, clear it and focus the input
        this.searchQuery = '';
        this.$refs.searchInput.focus();
        // If on mobile, also close the search bar
        if (window.innerWidth <= 768) {
          this.isSearchBarOpen = false;
        }
      } else {
        // If empty, focus the input
        this.$refs.searchInput.focus();
      }
    },
    openImageSearch() {
      // Check if user is logged in
      if (!this.isLoggedIn) {
        // Not logged in - always redirect to login
        this.showLoginModal = true;
        return;
      }
      
      // User is logged in - check if first time using image search
      const hasSeenImageSearchNotice = localStorage.getItem('hasSeenImageSearchNotice');
      
      if (!hasSeenImageSearchNotice) {
        // First time - mark as seen and show modal (modal will show acknowledge)
        localStorage.setItem('hasSeenImageSearchNotice', 'true');
      }
      
      // Show the image search modal
      this.showImageSearchModal = true;
    },
    closeImageSearch() {
      this.showImageSearchModal = false;
    },
    handleClickOutside(event) {
      // Close the autocomplete at any width. The mobile branch below only fires
      // under 768px and also clears the query, so it can't cover the desktop case.
      if (this.isSearchFocused) {
        const container = this.$refs.searchContainer;
        if (container && !container.contains(event.target)) {
          this.closeAutocomplete();
        }
      }

      // Only handle clicks when search bar is open and on mobile
      if (this.isSearchBarOpen && window.innerWidth <= 768) {
        // Check if click is outside search container and not on search icon
        const searchContainer = this.$refs.searchContainer;
        const searchIcon = document.querySelector('.mobile-search-icon');
        
        if (!searchContainer.contains(event.target) && !searchIcon.contains(event.target)) {
          this.isSearchBarOpen = false;
          this.searchQuery = '';
        }
      }

      // Handle profile dropdown click outside
      if (this.showProfileMenu) {
        const profileDropdown = document.querySelector('.profile-dropdown');
        const profileMenu = document.querySelector('.profile-menu');
        
        if (profileDropdown && profileMenu && 
            !profileDropdown.contains(event.target) && 
            !profileMenu.contains(event.target)) {
          this.showProfileMenu = false;
        }
      }
    },
    handleLoginSuccess() {
      // Add delay to show success message
      setTimeout(() => {
        this.showLoginModal = false;
        this.closeMobileMenu();
      }, 500); // 2 second delay
    },
    handleSignupSuccess() {
      // Add delay to show success message
      setTimeout(() => {
        this.showSignupModal = false;
        this.closeMobileMenu();
      }, 1000); // 2 second delay
    },
    toggleProfileMenu() {
      this.showProfileMenu = !this.showProfileMenu;
    },
    handleLogout() {
      this.closeMobileMenu();
      this.logout();
    },
    handleGlobalClick(event) {
      // Check if click is outside the mobile drawers and their toggle buttons
      const mobileMenu = event.target.closest('.mobile-menu');
      const menuButton = event.target.closest('.mobile-icon-btn');

      if (!mobileMenu && !menuButton && (this.isCategoryMenuOpen || this.isAccountMenuOpen)) {
        this.closeMobileMenu();
      }
    },
  }
}
</script>

<style>
* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

html, body, #app {
  height: 100%;
}

body {
  font-family: Arial, sans-serif;
  line-height: 1.6;
  color: #333;
}

#app {
  width: 100%;
  margin: 0;
  padding: 0;
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 100vh; /* ensure footer stays at bottom */
}

header {
  padding: 10px 0;
  border-bottom: 1px solid #eee;
  position: fixed;
  width: 100%;
  background: white;
  z-index: 1001;
  transition: all 0.3s ease;
  max-width: 100%;
}

nav {
  display: flex;
  /* Two groups rather than an even spread: logo + categories sit left, search and
     account controls sit right. `space-between` pushed the five category links out
     across the whole bar, which read as clutter. */
  justify-content: flex-start;
  align-items: center;
  gap: 18px;
  max-width: 1600px;
  margin: 0 auto;
  padding: 0 32px;
}

.logo {
  display: flex;
  align-items: center;
}

.logo a {
  display: flex;
  align-items: center;
  font-size: 1.5rem;
  font-weight: bold;
  color: #000;
  text-decoration: none;
  padding: 5px;
}

.logo img {
  height: 40px;
  width: auto;
  object-fit: contain;
}

.main-logo {
  display: block;
  height: 40px;
  width: auto;
  object-fit: contain;
}

.mini-logo {
  display: none;
}

/* --- Mobile icon set: one shared button style so the categories toggle,
   search, cart and account icons all read as the same family. Hidden on
   desktop; the 768px breakpoint below turns them on and hides the desktop nav. --- */
.mobile-icon-btn {
  display: none;
  align-items: center;
  justify-content: center;
  position: relative;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  color: #1a1a1a;
  text-decoration: none;
  transition: background-color 0.2s ease;
}

.mobile-icon-btn i {
  font-size: 16px;
  line-height: 1;
}

.mobile-icon-btn:active {
  background-color: #f0f0f0;
}

/* One shared gap for the search / cart / account trio. The buttons are all the
   same 34px box, so an even gap is what makes the icons read as evenly spaced —
   the previous 2px let the glyphs' own differing widths show as uneven rhythm. */
.mobile-icon-group {
  display: none;
  align-items: center;
  gap: 10px;
  margin-left: auto;
}

.mobile-categories-btn.active,
.mobile-account-btn.active {
  background-color: #f0f0f0;
  color: #1b4b8f;
}

/* The homepage hero slider's prev/next arrows sit outside the mobile drawer's
   own width and stayed visible (and clickable) behind it once a drawer was
   open. They belong to Home.vue, so this targets them globally rather than
   from inside that component's scoped styles. */
#app.mobile-drawer-open .nav-button {
  display: none;
}

/* Desktop Navigation */
.desktop-nav {
  display: flex;
  align-items: center;
  gap: 20px;
}

/* --- Main category nav (desktop) --- */
.category-nav {
  display: flex;
  align-items: center;
  gap: 22px;
  flex-shrink: 0;
  margin-left: 14px;
}

.category-nav-item {
  position: relative;
}

.category-nav-link {
  display: inline-block;
  padding: 10px 2px;
  color: #1a1a1a;
  font-size: 0.95rem;
  font-weight: 500;
  text-decoration: none;
  white-space: nowrap;
  border-bottom: 2px solid transparent;
}

.category-nav-link:hover,
.category-nav-link:focus-visible,
.category-nav-link.router-link-active {
  color: #1b4b8f;
  border-bottom-color: #1b4b8f;
}

.category-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  background: #fff;
  border: 1px solid #e6e6e6;
  border-radius: 6px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  z-index: 1002;
}

/* --- Main categories inside the mobile drawer --- */
.mobile-category-list {
  border-bottom: 1px solid #eee;
  padding-bottom: 8px;
  margin-bottom: 8px;
}

.mobile-category-header {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  background: none;
  border: none;
  font-size: 1rem;
  font-weight: 600;
  color: #1a1a1a;
  text-align: left;
  cursor: pointer;
}

.mobile-category-header i {
  font-size: 0.75rem;
  color: #888;
}

.mobile-category-brands {
  padding: 2px 0 10px;
  background: #fafafa;
}

/* Aligns the recursive NavCategoryNode rows with the flat .mobile-brand-item
   links below them — the nodes carry no horizontal padding of their own, so
   without this they sat flush against the drawer edge while the brand links
   were indented, which is what made the list look broken. The right padding
   leaves room for each node's chevron. */
.mobile-subcategories {
  padding: 0 8px 0 30px;
}

.mobile-brand-item {
  display: block;
  padding: 11px 16px 11px 30px;
  color: #444;
  font-size: 0.93rem;
  text-decoration: none;
}

.mobile-brand-item.is-all {
  font-weight: 600;
  color: #1b4b8f;
}

.mobile-brand-item:active {
  background: #eef2f8;
}

.mobile-auth-actions {
  display: flex;
  gap: 12px;
  padding: 16px;
}

.mobile-auth-actions button {
  flex: 1;
}

.search-container {
  display: flex;
  /* `margin-left: auto` absorbs the leftover row space, which is what splits the
     bar into a left group (logo + categories) and a right group (search + account).
     Width is a shrinkable cap rather than a hard 50% so the categories keep their
     room on narrower desktops. */
  width: 480px;
  max-width: 40%;
  min-width: 260px;
  flex: 0 1 auto;
  margin-left: auto;
  align-items: center;
  position: relative;
  z-index: 1001;
}

.search-input-wrapper {
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;
}

.search-icon-wrapper {
  position: absolute;
  left: 12px;
  z-index: 2;
  color: #666;
  cursor: pointer;
}

.search-icon-wrapper i {
  font-size: 16px;
  transition: all 0.3s ease;
}

.search-icon-wrapper i:hover {
  color: #333;
}

.search-container input {
  width: 100%;
  padding: 10px 40px 10px 35px;
  border: 1px solid #ccc;
  border-right: none;
  border-radius: 4px 0 0 4px;
  font-size: 16px;
  height: 42px;
  box-sizing: border-box;
}

.search-btn {
  padding: 0 20px;
  height: 42px;
  background: #000;
  color: #fff;
  border: none;
  border-radius: 0 4px 4px 0;
  cursor: pointer;
  font-weight: bold;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.camera-btn {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  padding: 5px;
  margin: 0;
  color: #999;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3;
}
.camera-btn i{font-size:18px}
.camera-btn:hover{color:#333;background:transparent}

.nav-links {
  display: flex;
  gap: 20px;
  align-items: center;
  position: relative;
}

.profile-menu {
  position: relative;
  cursor: pointer;
}

.username {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 5px 10px;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.username:hover {
  background-color: #f5f5f5;
}

.username i {
  font-size: 12px;
  transition: transform 0.2s;
}

.username.active i {
  transform: rotate(180deg);
}

.profile-dropdown {
  position: absolute;
  top: 100%;
  right: 0;
  background: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  min-width: 200px;
  z-index: 1002;
  display: none;
}

.profile-dropdown.show {
  display: block;
}

.profile-dropdown a {
  display: block;
  padding: 10px 15px;
  color: #333;
  text-decoration: none;
  transition: background-color 0.2s;
}

.profile-dropdown a:hover {
  background-color: #f5f5f5;
}

.profile-dropdown .divider {
  height: 1px;
  background-color: #eee;
  margin: 5px 0;
}

/* Left Side Navigation */
.profile-page-layout {
  display: flex;
  gap: 30px;
}

.profile-sidebar {
  width: 250px;
  padding: 20px;
  border-right: 1px solid #eee;
  /*height: calc(100vh - 80px);*/
  height: auto;
  position: fixed;
  background: white;
}

.profile-sidebar h2 {
  font-size: 1.2rem;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 1px solid #eee;
}

.profile-nav {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.profile-nav a {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px;
  color: #333;
  text-decoration: none;
  border-radius: 4px;
  transition: all 0.2s;
}

.profile-nav a:hover,
.profile-nav a.active {
  background-color: #f5f5f5;
  color: #0066cc;
}

.profile-nav i {
  width: 20px;
  text-align: center;
}

.profile-content {
  flex: 1;
  margin-left: 270px;
  padding: 20px;
}

.cart-icon {
  position: relative;
  cursor: pointer;
}

/* The 20px separator belongs to the desktop nav only. Scoped here because the
   mobile cart reuses .cart-icon inside .mobile-icon-group, where the group's
   own gap sets the rhythm — an extra margin there made the search-to-cart gap
   30px against the cart-to-account 10px. */
.nav-links .cart-icon {
  margin-left: 20px;
}

.cart-count {
  position: absolute;
  top: -8px;
  right: -8px;
  background: #e44d26;
  color: white;
  border-radius: 50%;
  width: 18px;
  height: 18px;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* The desktop cart uses the same bag glyph as the mobile header. */
.nav-links .cart-icon .fas.fa-shopping-bag {
  font-size: 20px;
  color: #333;
}

.admin-link {
  display: flex;
  align-items: center;
  gap: 5px;
}

.admin-link i {
  font-size: 16px;
}

main {
  padding-top: 75px;
  width: 100%;
  margin: 0;
  padding-left: 0;
  padding-right: 0;
  overflow-x: hidden;
  flex: 1 0 auto; /* allow main to grow so footer is pushed to bottom */
}

@media (max-width: 1506px) {
  header {
    /*width: 76%;*/
    width: 100%;
  }
}

@media (max-width: 1490px) {
  header {
    /*width: 88%;*/
    width: 100%;
  }
}
/*
@media (max-width: 1488px) {
  header {
    width: 80%;
  }
}*/

@media (max-width: 1294px) {
  header {
    /*width: 88%;*/
    width: 100%;
  }
}

@media (max-width: 1204px) {
  header {
    /*width: 95%;*/
    width: 100%;
  }
}

/* Below ~1200px the category bar, search box and account links can no longer share
   one row with usable spacing; the hamburger drawer carries the categories. */
@media (max-width: 1200px) {
  .category-nav {
    display: none;
  }

  .mobile-categories-btn {
    display: flex;
  }
}

/* Responsive Styles */
@media (max-width: 768px) {

  header {
    /*width: 90%;*/
    width: 100%;
  }

  /* Categories move into the left drawer below this width. */
  .category-nav {
    display: none;
  }

  /* Logo centers in the row regardless of how wide the left (categories) and
     right (search/cart/account) icon groups end up — `margin: 0 auto` on a
     flex item only centers when its neighbors are equal width, which they
     aren't here, so the logo is pulled out of flow and centered absolutely
     against the nav bar instead. */
  nav {
    position: relative;
    justify-content: space-between;
    padding: 0 12px;
    gap: 8px;
  }

  .logo {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    /* Reserve the actual width of both side groups (categories button + the
       three-icon group, each ~34px plus gaps, inside 12px nav padding) so the
       centered logo's box never overlaps them, and clip it as a fallback in
       case a longer logo still would. */
    max-width: calc(100% - 200px);
    overflow: hidden;
  }

  .mobile-categories-btn,
  .mobile-icon-group {
    position: relative;
    z-index: 2;
  }

  /* `.logo img` (element+class, specificity 0,1,1) otherwise beats a bare
     `.main-logo` class rule, so the desktop 40px height survives into mobile
     unless matched here at the same specificity. */
  .logo img.main-logo {
    display: block;
    height: 26px;
  }

  .mini-logo {
    display: none;
  }

  .logo a {
    margin-left: 0;
    padding: 0;
  }

  .mobile-categories-btn {
    display: flex;
  }

  .mobile-icon-group {
    display: flex;
  }

  .mobile-search-icon,
  .mobile-cart,
  .mobile-account-btn {
    display: flex;
  }

  .search-container {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    width: 100%;
    /* Undo the desktop right-group sizing; here the bar is a full-width overlay. */
    max-width: none;
    min-width: 0;
    margin-left: 0;
    background: white;
    padding: 10px 15px;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
    transform: translateY(-100%);
    transition: transform 0.3s ease;
    display: none;
  }

  .search-container.mobile-search-active {
    transform: translateY(0);
    display: flex;
  }

  .search-container input {
    border: 1px solid #ddd;
    border-right: none;
    padding: 12px 12px 12px 35px;
  }

  .search-btn {
    padding: 12px 20px;
  }

  .search-icon-wrapper {
    left: 15px;
  }

  .search-icon-wrapper i {
    font-size: 18px;
  }

  /* Desktop account/login links only show on the wide layout. */
  .desktop-nav {
    display: none;
  }

  /* Both drawers share the same fixed panel treatment; only the side + slide
     direction differ. */
  .mobile-drawer {
    position: fixed;
    top: 0;
    width: 80%;
    max-width: 300px;
    height: 100vh;
    background: white;
    padding: 20px;
    transition: transform 0.3s ease;
    z-index: 999;
    overflow-y: auto;
  }

  .mobile-drawer-left {
    left: 0;
    transform: translateX(-100%);
    box-shadow: 2px 0 5px rgba(0, 0, 0, 0.1);
  }

  .mobile-drawer-right {
    right: 0;
    transform: translateX(100%);
    box-shadow: -2px 0 5px rgba(0, 0, 0, 0.1);
  }

  .mobile-drawer.drawer-active {
    transform: translateX(0);
  }

  .mobile-menu-close {
    display: flex;
  }

  .profile-sidebar {
    display: none;  /* Hide sidebar on mobile */
  }

  .profile-content {
    margin-left: 0;  /* Remove margin since sidebar is hidden */
    padding: 15px;   /* Slightly reduce padding for mobile */
    width: 100%;     /* Take full width */
  }

  .profile-page-layout {
    padding-top: 60px;  /* Reduce top padding on mobile */
    display: block;     /* Switch to block layout */
  }

  /* Add heading styles for mobile */
  .profile-content h1,
  .profile-content .page-title {
    font-size: 1.25rem;  /* Approximately 20px */
    margin-bottom: 15px;
    font-weight: 500;
  }
}

/* Small mobile devices */
@media (max-width: 480px) {

  header {
    /*width: 90%;*/
    width: 100%;
  }

  .logo a {
    font-size: 1.2rem;
  }

  .mobile-drawer {
    width: 100%;
    max-width: none;
  }
}

/* Close Button Styles */
.close-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 10px;
  color: #333;
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.3s ease;
}

.close-btn:hover {
  color: #e44d26;
}

/* Mobile Menu Close Button: hidden until the 768px drawer layout turns it on. */
.mobile-menu-close {
  display: none;
  margin-left: auto;
}

.mobile-drawer-left .mobile-menu-close {
  margin-left: 0;
  margin-right: auto;
}

.username {
  margin-right: 15px;
  font-weight: 500;
  color: #333;
}

.logout-btn {
  background: none;
  border: none;
  color: #333;
  cursor: pointer;
  padding: 5px 10px;
  margin-right: 15px;
  font-size: 14px;
  transition: color 0.3s;
}

.logout-btn:hover {
  color: #007bff;
}

.login-btn,
.signup-btn {
  background: none;
  border: none;
  color: #333;
  cursor: pointer;
  padding: 5px 10px;
  margin-right: 15px;
  /* font-size: 14px; */
  font-size: 18px;
  transition: color 0.3s;
}

.login-btn:hover,
.signup-btn:hover {
  color: #007bff;
}

/* Remove the header-hidden class since we don't want to hide the header anymore */
.header-hidden {
  display: none;
}

/* Update z-index values for proper layering */
.modal-overlay {
  z-index: 2000;
}

/* Route transition spinner: same visual as the in-page spinners (CategoryListing,
   NewSelection), reused here so navigating to a lazy-loaded route isn't a blank
   screen while its chunk downloads. */
.route-loading-overlay {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.6);
  z-index: 1500;
}

.route-loading-overlay .spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

header {
  position: fixed;
  /*width: 75%;*/
  background: white;
  z-index: 1001;
  transition: all 0.3s ease;
}

/* Mobile Menu Styles */
.mobile-menu {
  padding: 20px 0;
}

.mobile-profile {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 15px 20px;
  border-bottom: 1px solid #eee;
  background: white;
}

.mobile-profile-image {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #eee;
  display: flex;
  align-items: center;
  justify-content: center;
}

.mobile-profile-image i {
  font-size: 20px;
  color: #666;
}

.mobile-profile-info {
  flex: 1;
}

.mobile-profile-name {
  font-weight: 500;
  margin-bottom: 4px;
}

.mobile-profile-view {
  color: #666;
  font-size: 14px;
  text-decoration: none;
}

.mobile-menu-list {
  display: flex;
  flex-direction: column;
}

.mobile-menu-item {
  padding: 15px 20px;
  display: flex;
  align-items: center;
  gap: 12px;
  color: #333;
  text-decoration: none;
  font-weight: 500;
  border-bottom: 1px solid #eee;
  background: white;
  transition: background-color 0.2s;
}

.mobile-menu-item:hover,
.mobile-menu-item.router-link-active {
  background-color: #f5f5f5;
  color: #0066cc;
}

.mobile-menu-item i {
  width: 20px;
  text-align: center;
  font-size: 16px;
}

@media (max-width: 768px) {
  .mobile-drawer {
    width: 85%;
    max-width: 320px;
  }

  .nav-links .cart-icon {
    display: none;  /* Hide desktop cart icon in mobile view */
  }

  /* Mobile cart badge sits on the smaller shared icon button. */
  .mobile-cart .cart-count {
    top: 2px;
    right: 2px;
    width: 16px;
    height: 16px;
    font-size: 10px;
  }
}

.mobile-menu-item.admin-link {
  background-color: #f8f9fa;
  border-top: 1px solid #eee;
  border-bottom: 1px solid #eee;
  color: #0066cc;
}

.mobile-menu-item.admin-link:hover {
  background-color: #e9ecef;
}

.logo-container {
  display: flex;
  align-items: center;
  gap: 10px;
}

.mini-logo-left {
  height: 30px;
  width: auto;
  object-fit: contain;
  margin-right: 20px;
}

@media (max-width: 768px) {
  .mini-logo-left {
    height: 25px;
    margin-right: 15px;
  }
}

/* Policy Links Styles */
.policy-links {
  position: fixed;
  bottom: 20px;
  left: 20px;
  display: flex;
  flex-direction: row;
  gap: 10px;
  z-index: 1000;
}

.policy-link {
  color: #666;
  text-decoration: none;
  font-size: 14px;
  transition: color 0.3s ease;
  background: rgba(255, 255, 255, 0.9);
  padding: 8px 12px;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.policy-link:hover {
  color: #333;
  background: white;
}

@media (max-width: 768px) {
  .policy-links {
    bottom: 10px;
    left: 10px;
    flex-direction: column;
  }
  .policy-link {
    font-size: 12px;
    padding: 6px 10px;
  }
}

.main-footer {
  width: 100%;
  background: #fff;
  border-top: 1px solid #eee;
  padding: 8px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 1000;
  box-sizing: border-box;
  font-size: 12px;
  min-height: 32px;
  flex-shrink: 0; /* don't let footer shrink */
}

.footer-links {
  display: flex;
  gap: 18px;
}

.footer-link {
  color: #111;
  text-decoration: none;
  font-size: 12px;
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  transition: color 0.2s;
  padding: 0 2px;
}

.footer-link:hover {
  color: #007bff;
}

.footer-copyright {
  color: #111;
  font-size: 12px;
  font-weight: bold;
  text-align: right;
}

@media (max-width: 768px) {
  .main-footer {
   /*flex-direction: column;
    align-items: flex-start;*/
    align-items: flex-end;
    padding: 8px 6px;
    gap: 6px;
    font-size: 11px;
    min-height: 28px;
  }
  .footer-links {
    gap: 10px;
    flex-wrap: wrap;
  }
  .footer-link {
    font-size: 11px;
    padding: 0 1px;
  }
  .footer-copyright {
    font-size: 11px;
    margin-top: 4px;
    text-align: left;
  }
}
</style> 